import { Dispatch, SetStateAction, useCallback, useEffect, useRef, useState } from 'react';

import useDebounce from '../../../../../hooks/useDebounce/useDebounce';
import useSearchParams from '../../../../../hooks/useSearchParams';
import {
  Option,
  OptionValue,
} from '../../components/MultiOptionsSelectorFilter/components/MultiOptionsSelector/components/OptionsList/optionsList.types';
import { CleanFilter, CommonFilterHookReturn, GetSearchParamKeys } from '../commonHookReturn.types';
import { MILLISECONDS_TO_APPLY_FILTER } from '../constants';

interface UseOptionsSelector {
  key: string;
  defaultValues?: string[] | string;
  shouldIgnoreDefaultFilters?: boolean;
  shouldIgnore?: boolean;
  debounceTime?: number;
  /**
   * @description TODO: TEMOS QUE DESACOPLAR ESSE HOOK DOS SEARCH PARAMS, O HOOK JUNTO DO COMPONENTE TEM OUTROS USE CASES QUE NÃO SÃO FILTROS (CUSTOMRE-V2)
   */
  dontHandleSearchParams?: boolean;
  isDisabled?: boolean;
}

interface UseOptionsSelectorOutput
  extends Omit<CommonFilterHookReturn, 'handleResetToDefaultValue'> {
  hasSelectedOptions: boolean;
  selectedOptions: any[];
  initialSelectedValuesFromSearchParams: OptionValue[];
  setSelectedOptions: Dispatch<SetStateAction<any[]>>;
  handleUpdateInternalState: (expandableState: boolean) => void;
  normalizeSelectedValues: (options: Option[]) => void;
  formatDefaultValues: (values: string | string[]) => string[];
}

const useOptionsSelector = ({
  key,
  defaultValues: initialValues,
  shouldIgnoreDefaultFilters = true,
  shouldIgnore,
  debounceTime,
  dontHandleSearchParams,
  isDisabled,
}: UseOptionsSelector): UseOptionsSelectorOutput => {
  const { removeSearchParams, searchParams, setSearchParams } = useSearchParams();

  const formatDefaultValues = (values: string | string[]): string[] => {
    const defaultValuesAsArray = Array.isArray(values) ? values : [values];
    return defaultValuesAsArray;
  };

  const getDefaultSelectedValues = () => {
    if (isDisabled) {
      return [];
    }
    const userDefaultValues = shouldIgnoreDefaultFilters ? searchParams[key] : initialValues;
    if (!userDefaultValues) {
      return [];
    }
    return formatDefaultValues(userDefaultValues);
  };

  const initialSelectedValuesFromSearchParams = useRef(getDefaultSelectedValues());

  const [hasSelectedOptions, setHasSelectedOptions] = useState(
    () => getDefaultSelectedValues().length > 0,
  );

  const [selectedOptions, setSelectedOptions] = useState(getDefaultSelectedValues());
  const debouncedSelectedOptions = useDebounce(
    selectedOptions,
    debounceTime || MILLISECONDS_TO_APPLY_FILTER,
  );

  useEffect(() => {
    if (shouldIgnore || dontHandleSearchParams) {
      return;
    }

    if (debouncedSelectedOptions.length <= 0) {
      removeSearchParams([key]);
    } else {
      setSearchParams({ [key]: debouncedSelectedOptions });
    }
  }, [debouncedSelectedOptions]);

  const handleUpdateInternalState = useCallback(
    (isExpandable: boolean) => {
      if (isExpandable) {
        return;
      }
      setHasSelectedOptions(!!selectedOptions.length);
    },
    [selectedOptions],
  );

  const normalizeSelectedValues = (options: Option[]) => {
    const valueOfOptions = options.map((option) => option.value);
    const valuesToExclude = selectedOptions.filter((value) => !valueOfOptions.includes(value));

    if (valuesToExclude.length) {
      setSelectedOptions((oldSelectedValues) =>
        oldSelectedValues.filter((value) => !valuesToExclude.includes(value)),
      );
    }
  };

  const cleanFilter: CleanFilter = () => {
    setSelectedOptions([]);
  };
  const getSearchParamKeys: GetSearchParamKeys = () => (isDisabled ? [] : key);

  return {
    hasSelectedOptions,
    selectedOptions,
    setSelectedOptions,
    handleUpdateInternalState,
    cleanFilter,
    getSearchParamKeys,
    normalizeSelectedValues,
    formatDefaultValues,
    initialSelectedValuesFromSearchParams: initialSelectedValuesFromSearchParams.current,
  };
};

export default useOptionsSelector;
